import { useCallback, useEffect, useRef, useState } from 'react';
import { useSalvaCampo } from './salvaCampo';
import { messaggioDa } from './notifiche';
import { anamnesiVociAPI } from '../services/api';
import { useUscitaProtetta } from './uscitaProtetta';

/**
 * La MODIFICA di una voce di anamnesi (o di una terapia) che si salva da
 * sola, campo per campo, quando se ne esce (regola di Alex del 24 settembre
 * 2026; schermate decise da Alex lo stesso giorno). Niente «Salva»: si
 * chiude con «Chiudi». La voce NUOVA resta un gesto («Aggiungi voce»,
 * «Aggiungi terapia»), perche' crea una riga firmata.
 *
 * Una copia sola per l'anamnesi e per le terapie, sopra il meccanismo
 * dell'anamnesi (utils/salvaCampo.js). Un campo che non e' cambiato non si
 * manda; il server fa lo stesso controllo per conto suo.
 *
 * @param onSalvato  dopo ogni scrittura riuscita (es. avvisare il riepilogo)
 * @param leggiValori  () => { campo: valore } | null — i valori che la modifica
 *        ha adesso a schermo. Allo SMONTAGGIO (cambio di linguetta della
 *        cartella, Ctrl K: nessun blur) quelli diversi dai salvati si
 *        scrivono: il testo non si perde (14 ottobre 2026). Se la scrittura
 *        fallisce a schermata chiusa non c'e' piu' un segno a cui tornare:
 *        lo dice una notifica.
 */
export function useModificaVoce(onSalvato, leggiValori = null) {
    const voceId = useRef(null);
    const salvati = useRef({});
    const [errori, setErrori] = useState({});

    const scrivi = useCallback(async (campo, valore) => {
        try {
            await anamnesiVociAPI.aggiorna(voceId.current, { [campo]: valore });
            salvati.current[campo] = String(valore ?? '');
            setErrori((p) => ({ ...p, [campo]: null }));
        } catch (e) {
            setErrori((p) => ({ ...p, [campo]: messaggioDa(e, 'il salvataggio non è riuscito') }));
            throw e;
        }
    }, []);
    /* Lo smontaggio (cambio di linguetta, Ctrl K: nessun blur) scrive i campi
       diversi dai salvati, con la stessa `esci` dell'uscita dal campo. */
    const valoriOra = useRef(null);
    valoriOra.current = leggiValori;
    const uscitaOra = useRef(null);
    const { stato, salva, nonSalvati, salvatoAlle, segnaBase, uscita } = useSalvaCampo(scrivi, onSalvato, {
        allaChiusura: () => {
            const valori = valoriOra.current ? valoriOra.current() : null;
            if (!valori || voceId.current === null) return;
            Object.entries(valori).forEach(([campo, valore]) => uscitaOra.current(campo, valore));
        },
    });
    /* ESCO CON UN CAMPO SCRITTO: PRIMA SI SALVA (Alex, 14/10/2026). La voce e'
       «sporca» anche quando il testo a schermo e' diverso da quello salvato e
       non e' ancora uscito dal campo, non solo quando un salvataggio e'
       fallito: cosi' GuardiaUscita e la scheda del paziente lo vedono, lo
       salvano e lo aspettano (al massimo ATTESA_SALVATAGGIO). Fino al 14/10 si
       dichiarava solo il rosso: con il server lento si usciva subito, senza
       domande e senza segno, e la scrittura arrivava dopo. Il confronto e'
       quello di `esci`, valore a schermo contro valore salvato. */
    const valoriADesso = valoriOra.current && voceId.current !== null ? valoriOra.current() : null;
    const diversoDalSalvato = !!valoriADesso && Object.entries(valoriADesso)
        .some(([campo, valore]) => String(valore ?? '') !== (salvati.current[campo] ?? ''));
    useUscitaProtetta(diversoDalSalvato || nonSalvati.length > 0, nonSalvati.length || null, uscita);

    /** Quando si apre la modifica: i valori di partenza sono quelli «salvati». */
    const inizia = useCallback((id, valori) => {
        voceId.current = id;
        salvati.current = Object.fromEntries(Object.entries(valori).map(([k, v]) => [k, String(v ?? '')]));
        setErrori({});
        /* Cio' che c'e' in archivio, perche' lo smontaggio scriva solo se e'
           cambiato. */
        Object.entries(valori).forEach(([k, v]) => segnaBase(k, v === '' ? null : v));
    }, [segnaBase]);

    const esci = (campo, valore) => {
        if (String(valore ?? '') === (salvati.current[campo] ?? '')) return;   // non e' cambiato
        salva(campo, valore);
    };

    uscitaOra.current = esci;

    const inCorso = Object.values(stato).includes('in_corso');
    const appenaSalvato = Object.values(stato).includes('salvato');
    const erroriAperti = nonSalvati.map((c) => ({ campo: c, messaggio: errori[c] || 'il salvataggio non è riuscito' }));

    return { inizia, esci, riprova: salva, inCorso, appenaSalvato, erroriAperti, nonSalvati, stato, salvatoAlle };
}
